import { BackLink, RADIUS, READING_MEASURE, WithHalfdot } from "@elyxeer/ui";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import {
  Link as RouterLink,
  Navigate,
  useNavigate,
  useParams,
} from "react-router-dom";

import { LegalDocumentView } from "~/components/legal/LegalDocumentView";
import { Screen } from "~/components/ui/Screen";
import { isLegalSlug, LEGAL_SLUGS, legalDocument } from "~/content/legal";
import { useLocale } from "~/i18n";
import { LEGAL_LABEL_KEYS, legalPath } from "~/lib/legal";
import { canGoBack } from "~/lib/navigation-depth";
import { useAuthStore } from "~/store/auth-store";

/**
 * A legal document, at a public URL.
 *
 * Outside every gate. Counsel requires these to be readable without logging in, an app
 * store reviewer arrives from a store listing rather than from inside the app, and the
 * code screen links here at the moment the member is asked to accept them — none of
 * which has a session behind it. It is registered in both route tables for that reason:
 * `AuthGate` answers everything else with the sign-in screen.
 *
 * It is also a full screen rather than a tab: this is the one place in the app where
 * the whole text has to be on the page, and the tab bar would only shorten it.
 */
export function LegalRoute() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { slug } = useParams();
  const authenticated =
    useAuthStore((state) => state.status) === "authenticated";

  // A slug we do not publish is a stale or mistyped link, and whoever followed it was
  // heading for the legal section — the terms are its front door, not the app's.
  if (!isLegalSlug(slug)) return <Navigate to={legalPath("terms")} replace />;

  const doc = legalDocument(slug, locale);
  if (!doc) return <Navigate to={legalPath("terms")} replace />;

  const others = LEGAL_SLUGS.filter((other) => other !== slug);

  return (
    <Screen maxWidth="lg" disableTopInset>
      <Box
        sx={{
          // The screen is reachable while signed out, where nothing above it has
          // cleared the notch.
          paddingTop: "calc(8px + env(safe-area-inset-top))",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 3, md: 4 },
        }}
      >
        <BackLink
          to={authenticated ? "/profile" : "/"}
          // Prefer a real history pop. The code screen carries the address and the
          // "this will create an account" flag in its location state; a push back to
          // its path would arrive without them and bounce the member out to the
          // e-mail step, having spent a code.
          onBack={canGoBack() ? () => navigate(-1) : undefined}
          label={t(authenticated ? "common.back" : "legal.backToSignIn")}
          testId="legal.back"
        />

        <Box sx={{ maxWidth: READING_MEASURE }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{doc.title}</WithHalfdot>
          </Typography>
          {doc.subtitle ? (
            <Typography
              variant="T2"
              sx={{ color: "text.secondary", marginTop: 1.5 }}
            >
              {doc.subtitle}
            </Typography>
          ) : null}
        </Box>

        <Box data-testid={`legal.document-${slug}`}>
          <LegalDocumentView document={doc} />
        </Box>

        {/* The documents cross-reference each other by name — the privacy policy sends
            the reader to the deletion instructions, the terms to both — so the other
            two are always one tap away rather than a trip back through the menu. */}
        <Paper
          variant="outlined"
          sx={{
            overflow: "hidden",
            maxWidth: READING_MEASURE,
            borderRadius: `${RADIUS.md}px`,
          }}
        >
          <List disablePadding>
            {others.map((other, index) => (
              <ListItemButton
                key={other}
                component={RouterLink}
                to={legalPath(other)}
                data-testid={`legal.related-${other}`}
                sx={{
                  gap: 1,
                  paddingBlock: 1.75,
                  paddingInline: 2.5,
                  borderTop: index === 0 ? "none" : "1px solid",
                  borderColor: "divider",
                }}
              >
                <ListItemText
                  primary={t(LEGAL_LABEL_KEYS[other])}
                  slotProps={{
                    primary: { variant: "L1", sx: { color: "text.primary" } },
                  }}
                />
                <ChevronRightIcon
                  sx={{
                    color: "text.muted",
                    flexShrink: 0,
                    fontSize: 24,
                    marginLeft: "auto",
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </Box>
    </Screen>
  );
}
